/**
 * Campaign updates — student- and admin-authored progress notes that appear
 * on the public campaign detail page.
 *
 * Two update authors:
 *   - `student` — posted by the campaign beneficiary from the campaign detail
 *                 page. Subject to moderation (donors and visitors can report
 *                 an update for violating platform guidelines).
 *   - `admin`   — posted by an admin from /admin/updates. Not user-reportable
 *                 (admin voice is trusted). Displays a small "From My Rich
 *                 Aunt" badge.
 *
 * Update states:
 *   - visible     Default state. Renders on the campaign detail feed.
 *   - reported    A donor/visitor has flagged this update. Still visible on
 *                 the public feed pending admin review (matches wall-report
 *                 policy — no silent takedowns).
 *   - removed     Admin removed the update after review. Hidden from the
 *                 public feed but retained in the record for audit.
 *
 * State is persisted to localStorage for the prototype so the pattern
 * survives page reloads. When the backend arrives, swap the four exports
 * for API calls.
 */

const STORAGE_KEY = "mra.campaignUpdates.v1";

const SEED = [
  // Student-authored update on Maya R. campaign (campaignId matches
  // CAMPAIGNS array index in content.js).
  {
    id: "u-100",
    campaignId: 0,
    author: "student",
    authorName: "Maya",
    body:
      "Update from Maya: I hit 84% of the goal this week — thank you to everyone who's chipped in. " +
      "I take the LSAT in seven days. Groceries and prep books are covered because of you.",
    postedAt: "2026-02-24T14:12:00Z",
    state: "visible",
  },
  {
    id: "u-101",
    campaignId: 0,
    author: "admin",
    authorName: "My Rich Aunt",
    body:
      "Maya's campaign is enrollment-verified and eligible for a supplemental grant of up to $500 — the beta launch cap, which will be increased after we've raised funds for a full pilot launch. Grants are disbursed weekly.",
    postedAt: "2026-02-22T09:00:00Z",
    state: "visible",
  },
  {
    id: "u-102",
    campaignId: 1,
    author: "student",
    authorName: "Andre",
    body:
      "Update from Andre: made it to the second-round interview at the summer research lab. " +
      "The winter coat you all pitched in for is very much earning its keep this week.",
    postedAt: "2026-02-23T18:45:00Z",
    state: "visible",
  },
  {
    id: "u-103",
    campaignId: 2,
    author: "student",
    authorName: "Zohran",
    body:
      "My study abroad semester in Nairobi is set: program fee paid, visa in hand, flight booked for the spring. " +
      "Every donor here has my thanks, and my first postcard from Nairobi.",
    postedAt: "2026-02-22T21:30:00Z",
    state: "reported",   // <- a visitor flagged this update; admin review pending
    reportedAt: "2026-02-24T11:04:00Z",
    reportReason: "Feels off-topic",
  },
];

/* -------------------------------------------------------------------------- */
/* Storage helpers                                                            */
/* -------------------------------------------------------------------------- */

const read = () => {
  if (typeof window === "undefined") return { updates: [...SEED] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { updates: [...SEED] };
    const parsed = JSON.parse(raw);
    return { updates: Array.isArray(parsed.updates) ? parsed.updates : [...SEED] };
  } catch {
    return { updates: [...SEED] };
  }
};

const write = (store) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* localStorage full or unavailable — ignore in prototype */
  }
  listeners.forEach((cb) => cb(store));
};

const listeners = new Set();

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

// All updates for a given campaign, filtered to visible-or-reported
// (removed updates stay in the record but do not appear on the public feed).
export const getUpdatesForCampaign = (campaignId) => {
  const { updates } = read();
  return updates
    .filter((u) => u.campaignId === campaignId && u.state !== "removed")
    .sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
};

// Every update across every campaign — admin surfaces call this and filter.
export const getAllUpdates = () => {
  const { updates } = read();
  return [...updates].sort((a, b) => new Date(b.postedAt) - new Date(a.postedAt));
};

export const getReportedUpdates = () => {
  const { updates } = read();
  return updates
    .filter((u) => u.state === "reported")
    .sort((a, b) => new Date(a.reportedAt || a.postedAt) - new Date(b.reportedAt || b.postedAt));
};

// Student or admin posts a new update.
// author: "student" | "admin"
export const postUpdate = ({ campaignId, author, authorName, body }) => {
  const store = read();
  const id = `u-${Date.now()}`;
  const next = {
    ...store,
    updates: [
      ...store.updates,
      {
        id,
        campaignId,
        author,
        authorName,
        body,
        postedAt: new Date().toISOString(),
        state: "visible",
      },
    ],
  };
  write(next);
  return id;
};

// A visitor flags a student-authored update.
// Admin updates cannot be reported.
export const reportUpdate = (id, reason = "") => {
  const store = read();
  const updates = store.updates.map((u) =>
    u.id === id && u.author === "student"
      ? { ...u, state: "reported", reportedAt: new Date().toISOString(), reportReason: reason }
      : u,
  );
  write({ updates });
};

// Admin decides to remove an update after review.
export const removeUpdate = (id, moderatorNote = "") => {
  const store = read();
  const updates = store.updates.map((u) =>
    u.id === id ? { ...u, state: "removed", removedAt: new Date().toISOString(), moderatorNote } : u,
  );
  write({ updates });
};

// Admin decides the reported update is fine and keeps it live.
export const keepUpdate = (id, moderatorNote = "") => {
  const store = read();
  const updates = store.updates.map((u) =>
    u.id === id ? { ...u, state: "visible", keptAt: new Date().toISOString(), moderatorNote } : u,
  );
  write({ updates });
};

export const subscribeUpdates = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
